import { Card } from '../Models/Card'
import { CardsState } from '../Models/CardsState'
import { AppState, CurrentAppState } from '../Models/AppState'
import { themes } from './themes'
import { loadSettings } from './settingsManager'
import { saveGameState, loadGameState, clearGameState } from './stateManager'
import { invalidateAutoCompleteCache, clearAutoCompleteSnapshot } from './autocompleteGame'
import { deepClone } from './utils/deepClone'
import { assignCardCoordinates } from './assignCardCoordinates'
import { timerStart, timerIsStarted } from './timer'
import { validateCardIntegrity } from './cardIntegrity'

// Load settings on startup
const initialSettings = loadSettings()

// Helper function to safely get theme
function getThemeByName(themeName: string) {
    return themes[themeName as keyof typeof themes] ?? themes.HighContrastTheme
}

export let cardState: CardsState[] = []
export let appState: AppState = {
    NUM_TABLEAU: 7,
    NUM_STOCK: 1,
    NUM_WASTE: 1,
    NUM_FOUNDATIONS: 4,
    NUM_CARDS_FLIPPED: initialSettings.drawCount,
    cardWidth: undefined,
    cardHeight: undefined,
    windowHeight: undefined,
    windowWidth: undefined,
    cardSpacing: undefined,
    cardYOffset: undefined,
    foundationXOffset: undefined,
    // Load theme from settings, fallback to HighContrastTheme if not found
    currentTheme: getThemeByName(initialSettings.themeName),
    currentAppState: CurrentAppState.Playing,
    settings: initialSettings,
    isStandalone: false, // Set during pageSetup() based on environment detection
    showWinRecords: false,
    showMenuRecords: false,
}

export function setCardWidth(width: number) {
    appState.cardWidth = width
}

export function setCardHeight(height: number) {
    appState.cardHeight = height
}

export function setCardState(newState: CardsState, opts?: { skipIntegrity?: boolean }) {
    const newCardState: CardsState[] = [...cardState, newState]
    cardState = newCardState

    // Safety net: verify all 52 cards are accounted for after each state change.
    // If corruption is detected, undo() reverts to the previous state.
    // Tests that seed partial states pass skipIntegrity to bypass validation entirely.
    validateCardIntegrity(opts?.skipIntegrity)

    // Save state to localStorage
    saveGameState(cardState)

    invalidateAutoCompleteCache()
}

export function setAppState(newState: AppState) {
    appState = newState
    // Keep window.appState in sync for debugging/testing
    if (typeof window !== 'undefined') {
        //@ts-ignore
        window.appState = appState
    }
}

export function resetCardState() {
    cardState = []
    _undoCount = 0
    clearAutoCompleteSnapshot()
    // Clear saved state from localStorage when starting new game
    clearGameState()
    invalidateAutoCompleteCache()
}

/**
 * Restores the game state from localStorage
 * @returns true if state was restored, false otherwise
 */
export function restoreCardState(): boolean {
    const savedState = loadGameState()
    if (savedState && savedState.length > 0) {
        cardState = savedState
        invalidateAutoCompleteCache()
        return true
    }
    return false
}

let draggedCards: Card[] = []
export function getDraggedCards() {
    return draggedCards
}

/**
 * Keep only the current card state and drop all undo history.
 * Used at hard game boundaries (auto-complete start, game complete)
 * so Undo cannot jump into an older session.
 */
export function clearUndoHistory() {
    if (cardState.length === 0) {
        clearAutoCompleteSnapshot()
        invalidateAutoCompleteCache()
        return
    }

    cardState = [cardState[cardState.length - 1]]
    draggedCards = []
    clearAutoCompleteSnapshot()
    saveGameState(cardState)
    invalidateAutoCompleteCache()
}

// ─── Undo count tracking ────────────────────────────────────────────────────

/** Number of intentional undo button presses in the current game. */
let _undoCount = 0

/** Get the current undo count for this game. */
export function getUndoCount(): number {
    return _undoCount
}

/** Increment the undo count (call only for intentional undo button presses). */
export function incrementUndoCount(): void {
    _undoCount++
}

// ─── Dragged cards ──────────────────────────────────────────────────────────

export function setDraggedCards(cards: Card[]) {
    const coordinatesSaved = cards.map((card) => {
        if (
            card.originalX === undefined ||
            card.originalX === null ||
            card.originalY === undefined ||
            card.originalY === null
        ) {
            card.originalX = card.x
            card.originalY = card.y
        }
        return card
    })
    draggedCards = coordinatesSaved
}

export function undo() {
    if (cardState.length > 1) {
        cardState.pop()
        // Clear any cards being dragged
        draggedCards = []
        // Persist the reverted state to localStorage
        saveGameState(cardState)
        invalidateAutoCompleteCache()
    }
}

/**
 * Silently pops the last state without clearing dragged cards or saving.
 * Used during drag-and-drop to collapse the pickup + drop into a single history entry.
 */
function popState() {
    if (cardState.length > 1) {
        cardState.pop()
        invalidateAutoCompleteCache()
    }
}

/**
 * Clone current state, apply mutations, assign coordinates, and commit.
 *
 * @param mutate  – callback that receives the cloned state for in-place mutation
 * @param opts.popFirst – if true, pop the previous state before committing
 *                        (used for drag-drop to collapse pickup+drop into one undo step)
 * @param opts.source – optional source state to clone instead of getCurrentCardState()
 * @param opts.skipAutoFlip – if true, skip the auto-flip of exposed face-down cards
 *                            (used during card pickup so the flip waits until the drop)
 * @param opts.skipIntegrity – if true, skip the 52-card integrity check for this commit
 *                             (used during card pickup when cards are temporarily in transit
 *                              between the state and draggedCards)
 * @returns the committed state (with coordinates assigned), useful for post-commit
 *          work like animations
 */
export function commitMove(
    mutate: (state: CardsState) => void,
    opts?: { popFirst?: boolean; source?: CardsState; skipAutoFlip?: boolean; skipIntegrity?: boolean }
): CardsState {
    // Start the game timer on the very first move
    if (!timerIsStarted()) {
        timerStart()
    }

    const newState: CardsState = deepClone(opts?.source ?? getCurrentCardState())
    mutate(newState)

    // Auto-flip any exposed face-down cards at the top of tableau columns,
    // unless explicitly skipped (e.g. during pickup — flip waits for drop)
    // or unless the user has disabled auto-flip in settings.
    if (!opts?.skipAutoFlip && appState.settings.autoFlipCards) {
        for (const pile of newState.tableau) {
            if (pile.length > 0 && pile[pile.length - 1].isFaceDown) {
                pile[pile.length - 1].isFaceDown = false
            }
        }
    }

    const committed: CardsState = assignCardCoordinates(newState)
    if (opts?.popFirst) popState()
    setCardState(committed, { skipIntegrity: opts?.skipIntegrity })
    return committed
}

export function setWindowHeight(height: number) {
    appState.windowHeight = height
}

export function setWindowWidth(width: number) {
    appState.windowWidth = width
}

export function setCardSpacing(spacing: number) {
    appState.cardSpacing = spacing
}
export function setCardYOffset(offset: number) {
    appState.cardYOffset = offset
}
export function setFoundationXOffset(offset: number) {
    appState.foundationXOffset = offset
}

export function checkForWin(): boolean {
    const foundations = getCurrentCardState().foundation
    for (const foundation of foundations) {
        if (foundation.length !== 13) {
            return false
        }
        // Verify card order: Ace through King with consistent suit
        for (let j = 0; j < foundation.length; j++) {
            if (foundation[j].numValue !== j + 1) {
                return false
            }
            if (foundation[j].type !== foundation[0].type) {
                return false
            }
        }
    }
    return true
}

let undoImageSource: HTMLOrSVGImageElement
export function setUndoImageSource(src: HTMLOrSVGImageElement) {
    undoImageSource = src
}

let newGameButtonImageSource: HTMLOrSVGImageElement
export function setNewGameButtonImageSource(src: HTMLOrSVGImageElement) {
    newGameButtonImageSource = src
}

let menuButtonImageSource: HTMLOrSVGImageElement
export function setMenuImageSource(src: HTMLOrSVGImageElement) {
    menuButtonImageSource = src
}

export let cardBackImageSource: HTMLOrSVGImageElement
export function setCardBackImageSource(src: HTMLOrSVGImageElement) {
    cardBackImageSource = src
}

export function getCurrentCardState(): CardsState {
    return cardState[cardState.length - 1]
}
